import { Checkbox, IconButton, SelectChangeEvent, Stack, styled, Typography } from '@mui/material';
import { AddIcon } from 'assets';
import { InputText } from 'app/components/elements/InputText';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import React, { useCallback, useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormOptionPropsSchema } from 'utils/validate';
import { startCase, toLower } from 'lodash';
import { CloseOutlined } from '@mui/icons-material';
import { InputSelect } from 'app/components/elements/InputSelect';
import { inspectionOptions } from './ModalNewDropdown';

type FormOptionProps = {
  options: {
    name: string;
    value: string;
  }[];
  title: string;
  type: string[];
  id: number;
  onsubmit: (value: TFormOption) => void;
  onDeleteDropdown: (id: number) => void;
  required: boolean;
  clientIds?: number[];
  clientOptions?: { value: string; label: string }[];
};

export type TFormOption = {
  options: {
    id?: number;
    name: string;
    value: string;
  }[];
  deleteOptionIds?: Record<string, number[]>;
  groupName: string;
  id: number;
  type: string[];
  required: boolean;
  clientIds?: number[];
};
export const FormOption = ({
  options,
  title,
  type,
  id,
  onsubmit,
  onDeleteDropdown,
  required,
  clientIds: initialClientIds,
  clientOptions
}: FormOptionProps) => {
  const [selectedClientIds, setSelectedClientIds] = React.useState<string[]>(
    initialClientIds?.map(String) || []
  );
  const [clientsDirty, setClientsDirty] = React.useState(false);
  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    reset,
    register,
    formState: { errors, isDirty }
  } = useForm<TFormOption>({
    defaultValues: { options, deleteOptionIds: {}, groupName: title, type: [], id, required },
    resolver: yupResolver(FormOptionPropsSchema)
  });

  useEffect(() => {
    reset({ options, deleteOptionIds: {}, groupName: title, type, id, required });
    setSelectedClientIds(initialClientIds?.map(String) || []);
    setClientsDirty(false);
  }, [options, reset, title, type, id, required, initialClientIds]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options' as never
  });

  const onDleteOption = useCallback(
    (index: number) => {
      const value = getValues(`options.${index}`);
      const deleteOptionIds = getValues('deleteOptionIds');
      if (window.confirm(`Are you sure you want to delete "${value.value}"?`)) {
        const _deleteOptionIds = JSON.parse(JSON.stringify(deleteOptionIds));
        if (value.id) {
          if (!_deleteOptionIds['groupName']) {
            _deleteOptionIds['groupName'] = [];
          }
          _deleteOptionIds['groupName'].push(value.id);
          setValue('deleteOptionIds', _deleteOptionIds);
        }
        remove(index);
      }
    },
    [getValues, remove, setValue]
  );

  const ListOption = useCallback(
    () => (
      <>
        <input hidden {...register('id')} />
        {fields.map((_, index) => {
          return (
            <Controller
              name={`options.${index}.value`}
              control={control}
              render={({ field }) => {
                return (
                  <div style={{ position: 'relative' }}>
                    <InputText
                      inputError={errors.options?.[index]?.value?.message}
                      placeholder={'Name'}
                      inputProps={{
                        ...field,
                        disabled: watch(`options.${index}.value`) === 'OTHER'
                      }}
                    />
                    {watch(`options.${index}.value`) !== 'OTHER' && (
                      <CloseOutlined
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '16px',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          onDleteOption(index);
                        }}
                      />
                    )}
                  </div>
                );
              }}
            />
          );
        })}
      </>
    ),
    [fields, control, errors.options, watch, onDleteOption, register]
  );

  return (
    <WrapperItem>
      <Stack
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        <Title
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => {
            setValue('groupName', e.currentTarget?.innerText, {
              shouldDirty: true
            });
          }}
          {...register('groupName')}>
          {startCase(toLower(title))}
        </Title>
        <IconButton
          onClick={() => {
            append({ name: title, value: '' });
          }}>
          <AddIcon color={'#000'} />
        </IconButton>
      </Stack>
      <Stack
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        <Title>Is Required</Title>
        <Checkbox checked={getValues('required')} {...register('required')} />
      </Stack>
      {clientOptions && (
        <InputSelect
          multiple
          options={clientOptions}
          value={selectedClientIds}
          title="Clients"
          placeholder="Select clients"
          onChange={(e) => {
            setSelectedClientIds(e.target.value as unknown as string[]);
            setClientsDirty(true);
          }}
        />
      )}
      <Stack gap={1.25} my={2.5} overflow={'auto'} flex={1}>
        <Controller
          control={control}
          render={({ field: { onChange, value } }) => (
            <InputSelect
              title={'Select Type'}
              inputError={errors.type?.message || ''}
              multiple
              value={value}
              placeholder={'Select Type'}
              onChange={(e: SelectChangeEvent<unknown>) => {
                onChange(e.target.value, { shouldDirty: true });
              }}
              options={inspectionOptions}
            />
          )}
          name={`type`}
        />
        <ListOption />
      </Stack>
      <Stack style={{ flexDirection: 'row', gap: '20px' }}>
        <ButtonCustom
          label={'Cancel'}
          isSecondary
          onClick={() => {
            reset({ options, groupName: title, type, id, required });
            setValue('deleteOptionIds', {});
            setSelectedClientIds(initialClientIds?.map(String) || []);
            setClientsDirty(false);
          }}
          disabled={!isDirty && !clientsDirty}
        />
        <ButtonCustom label={'Delete'} onClick={() => onDeleteDropdown(getValues('id'))} />
        <ButtonCustom
          disabled={!isDirty && !clientsDirty}
          label={'Save'}
          onClick={handleSubmit((data) => {
            onsubmit({
              ...data,
              clientIds: selectedClientIds.length ? selectedClientIds.map(Number) : []
            });
            setClientsDirty(false);
          })}
        />
      </Stack>
    </WrapperItem>
  );
};
const WrapperItem = styled(Stack)(() => ({
  padding: 20,
  border: `1px solid #818181`,
  borderRadius: 10,
  height: 'calc(100vh / 3 * 2)',
  boxShadow: 'inset 0 5px 10px rgba(0, 0, 0, 0.05)'
}));
const Title = styled(Typography)(() => ({
  fontSize: 20,
  fontWeight: 700,
  ':focus': {
    padding: '14px 12px',
    width: '100%'
  }
}));
